import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MsalService } from '@azure/msal-angular';
import dxDropDownButton, { InitializedEvent } from 'devextreme/ui/drop_down_button';

import { CodeValueGroups, NavigationService, PhxConstants, PhxLocalizationService, WindowRefService } from '@common';
import { PhxModalComponent } from '@common/components/phx-modal/phx-modal.component';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { UserContext, UserProfile, UserProfilePerDbInstance } from '@common/model';
import { AuthService } from '@common/services/auth.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';
import { ConnectivityService } from '@common/services/connectivity.service';
import { EventService, PhxEvents } from '@common/services/event.service';
import { UserBehaviorService } from '@common/services/user-behavior.service';
import { ConfigurationService } from '@configuration/service/configuration.service';

import { TopNavMenuResourceKeys } from './top-nav-menu-resource-keys';

interface LocalProfile extends UserProfilePerDbInstance {
  Label?: string;
}

@Component({
  selector: 'app-top-nav-menu',
  templateUrl: './top-nav-menu.component.html',
  styleUrls: ['./top-nav-menu.component.less'],
  standalone: false
})
export class TopNavMenuComponent extends BaseComponentOnDestroy implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly localizationService = inject(PhxLocalizationService);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly eventService = inject(EventService);
  private readonly msalService = inject(MsalService);
  readonly winRef = inject(WindowRefService);
  private readonly userBehaviorService = inject(UserBehaviorService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly connectivityService = inject(ConnectivityService);

  @ViewChild('pickProfileModal', { static: true })
  pickProfileModal: PhxModalComponent;

  readonly isOnline$ = this.connectivityService.isOnline$;

  fullName: string;
  currentProfile: UserProfile;
  currentProfileId: number;
  currentDatabaseId: number;
  currentInstanceName: string;
  moreThanOneProfiles: boolean;

  profiles: LocalProfile[] = [];
  profilesGroupedByInstance: { instanceName: string; fullName: string; profiles: LocalProfile[]; }[] = [];
  multiProfile = false;
  multipleTenants = false;

  menuOpen = false;
  isMobile = false;

  readonly codeValueGroups = CodeValueGroups;
  resourceKeys = TopNavMenuResourceKeys;
  showProductboardPortal = false;
  hideSupportTicketMenuItem = false;
  showRrwebRecording = false;
  profileMenuDropdownButton?: dxDropDownButton;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateIsMobile();
  }

  ngOnInit() {
    this.updateIsMobile();
    this.authService.getUserContext().then(user => {
      this.fullName = user.User.PreferredFirstName + ' ' + user.User.PreferredLastName;
    });

    this.hideSupportTicketMenuItem = this.configurationService.isFeatureActive(PhxConstants.FeatureFlags.HideSupportTicketMenuItem);

    forkJoin([
      this.authService.getCurrentProfile(),
      this.authService.getCurrentUser(),
      this.authService.getUserContext()
    ]).subscribe(([currentProfile, userInfo, userContext]) => {
      this.showRrwebRecording = this.configurationService.isFeatureActive(PhxConstants.FeatureFlags.RrwebRecording)
        && currentProfile.ProfileTypeId === PhxConstants.UserProfileType.Internal;
      this.multipleTenants = (new Set(userInfo.Profiles.map(m => m.DatabaseId))).size > 1;

      const tenantName = userContext.User.CurrentTenantName;
      const tenantLabel = PhxConstants.TenantDisplayName[tenantName] ?? tenantName;

      this.fullName = `${this.multipleTenants ? tenantLabel + ' - ' : ''} ${userContext.User.PreferredFirstName} ${userContext.User.PreferredLastName}`;

      this.currentProfile = currentProfile;
      this.currentProfileId = currentProfile.Id;
      this.currentDatabaseId = currentProfile.DatabaseId;
      this.moreThanOneProfiles = userInfo.Profiles?.length > 1;

      const firstDbInstanceProfile = userInfo.Profiles.find(x => x.DatabaseId === this.currentDatabaseId);
      this.currentInstanceName = firstDbInstanceProfile != null ? firstDbInstanceProfile.InstanceName : '';

      /** NOTE: profile ids across tenants/database can be the same - filter by profile id and database id */
      const sortedDbProfiles = this.sortProfiles(userInfo.Profiles).filter(f => !(f.ProfileId === this.currentProfileId && f.DatabaseId === this.currentDatabaseId));
      this.profiles = sortedDbProfiles.map(profile => {
        return {
          ...profile,
          Label: this.multipleTenants ? `${profile.InstanceName} - ${profile.FirstName} ${profile.LastName}` : ''
        };
      });

      this.multiProfile = this.profiles.length > 1;

      this.profilesGroupedByInstance = this.GroupBy(userInfo.Profiles, 'DatabaseId').map(group => {
        return {
          instanceName: group.value[0].InstanceName,
          fullName: `${group.value[0].FirstName} ${group.value[0].LastName}`,
          profiles: group.value
        };
      });
    });
  }

  /* We have to close it, as the dropdown part gets scrolled with the page,
     could be the result of float position on many of the grandparent elements
  */
  @HostListener('window:scroll', [])
  closeProfileDropdown(): void {
    void this.profileMenuDropdownButton?.close();
  }

  onProfileMenuDropDownInitialized = (e: InitializedEvent): void => {
    this.profileMenuDropdownButton = e.component;
  };

  updateIsMobile() {
    this.isMobile = window.innerWidth <= 1279;
  }

  closeMenu() {
    this.onMenuOpenChange(false);
  }

  onMenuOpenChange(value: boolean) {
    this.menuOpen = value;
    this.navigationService.updateTopNavState(value);
  }

  openPickProfileModal() {
    this.pickProfileModal.show();
  }

  setProfile(profile: LocalProfile) {
    this.closeMenu();
    const oldDatabaseId = this.currentDatabaseId;
    const oldProfileId = this.currentProfileId;

    this.currentDatabaseId = profile.DatabaseId;
    this.currentProfileId = profile.ProfileId;

    if (this.multipleTenants) {
      this.fullName = profile.Label;
    }

    // Leaves the current tenant's who's-viewing list before the switch replaces its token.
    void this.userBehaviorService.clearUserSessionBehaviors();
    this.authService
      .setCurrentProfile(profile.DatabaseId, profile.ProfileId)
      .then((userContext: UserContext) => {
        const p = userContext.User.UserProfiles.find(x => x.Id === profile.ProfileId);
        if (p) {
          this.currentProfile = p;
        }
        if (p.ProfileTypeId === PhxConstants.UserProfileType.Internal) {
          Promise.all([
            this.router.navigate(['/next/account/home']),
            this.userBehaviorService.clearUserSessionBehaviors()
          ]).then(() => {
            window.location.reload();
          });
        } else {
          Promise.all([
            this.router.navigate(['/next/activity-centre']),
            this.userBehaviorService.clearUserSessionBehaviors()
          ]).then(() => {
            window.location.reload();
          });
        }
        this.eventService.trigger(PhxEvents.ProfileChanged, this.currentProfile);
      })
      .catch(() => {
        this.currentDatabaseId = oldDatabaseId;
        this.currentProfileId = oldProfileId;
        this.router.navigate(['/next/activity-centre']);
      });
  }

  logoff() {
    this.closeMenu();
    Promise.all([
      this.localizationService.loadAnonymousTranslations(),
      this.userBehaviorService.clearUserSessionBehaviors()
    ]).finally(() => {
      this.authService.logout();
      if (this.msalService.instance.getAllAccounts().length > 0) {
        this.msalService.logout();
      }
      this.winRef.openUrl('/home', '_self');
      this.broadcastChannelService.sendMessage(
        this.broadcastChannelService.actions.channelTabsRefresh
      );
    });
  }

  public GroupBy<T>(value: Array<T>, field: string): Array<{ key: string; value: T[]; }> {
    const groupedObj = value.reduce((prev, cur) => {
      if (!prev[cur[field]]) {
        prev[cur[field]] = [cur];
      } else {
        prev[cur[field]].push(cur);
      }
      return prev;
    }, {});
    return Object.keys(groupedObj).map(key => ({ key, value: groupedObj[key] }));
  }

  public broadcastSetProfileEvent(): void {
    this.broadcastChannelService.sendMessage(
      this.broadcastChannelService.actions.channelTabsRefresh
    );
  }

  private sortProfiles(profiles: UserProfilePerDbInstance[]) {
    return profiles.sort((a, b) => {
      const diffDatabaseId = (a.DatabaseId === this.currentDatabaseId ? -1 : a.DatabaseId)
        - (b.DatabaseId === this.currentDatabaseId ? -1 : b.DatabaseId);
      const diffProfileTypeId = a.ProfileTypeId - b.ProfileTypeId;

      return diffDatabaseId !== 0 ? diffDatabaseId : diffProfileTypeId;
    });
  }
}
